import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import BackupRoundedIcon from '@mui/icons-material/BackupRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import StoreBadges from '../../components/StoreBadges/StoreBadges';
import PhoneShot from '../../components/PhoneShot/PhoneShot';
import { audiences, extras, faqs, freeFeatures, popularSchedules, premiumFeatures, showcases, steps } from '../../data/shiftwaveContent';
import { copapp, qrCodeUrl, shiftwave } from '../../utils/downloads';
import './Home.scss';

const extraIcons: Record<string, React.ReactNode> = {
    event: <EventAvailableRoundedIcon />,
    backup: <BackupRoundedIcon />,
    photo: <PhotoLibraryRoundedIcon />,
    qr: <QrCode2RoundedIcon />,
    devices: <DevicesRoundedIcon />,
    lock: <LockRoundedIcon />,
};

const PremiumTag: React.FC = () => (
    <span className="premium-tag">
        <StarRoundedIcon /> Premium
    </span>
);

const Home: React.FC = () => {
    const location = useLocation();
    const firstRender = useRef(true);

    // Scroll to #section links from the header. Jump instantly when the page
    // is opened with a hash; animate when navigating within the site.
    useEffect(() => {
        const behavior: ScrollBehavior = firstRender.current ? 'auto' : 'smooth';
        firstRender.current = false;
        if (!location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior, block: 'start' });
    }, [location]);

    return (
        <div className="home">
            {/* ── Hero ─────────────────────────────────────────────── */}
            <section className="hero">
                <Container maxWidth="lg" className="hero__inner">
                    <div className="hero__copy">
                        <div className="hero__app">
                            <img src={shiftwave.icon} alt="" width={56} height={56} />
                            <div>
                                <strong>ShiftWave</strong>
                                <span>Repeating Schedule Calendar</span>
                            </div>
                        </div>
                        <h1>
                            Every shift.
                            <br />
                            <span className="hero__accent">At a glance.</span>
                        </h1>
                        <p className="hero__lead">
                            <strong>Throw away your paper schedule.</strong> ShiftWave turns any rotating schedule into
                            a color-coded calendar on your phone. Set up your rotation once, or load it with a 4-digit
                            code, and never count days on your fingers again.
                        </p>
                        <StoreBadges app={shiftwave} placement="hero" />
                        <p className="hero__meta">Free download · iPhone, iPad &amp; Android</p>
                    </div>

                    <div className="hero__visual" aria-hidden="true">
                        <div className="hero__glow" />
                        <PhoneShot shot={1} crop priority alt="" className="hero__phone" />
                    </div>
                </Container>
            </section>

            {/* ── Who it's for + proof points ─────────────────────── */}
            <section className="proof">
                <Container maxWidth="lg">
                    <p className="proof__title">Built for everyone who works a rotation</p>
                    <ul className="proof__audiences">
                        {audiences.map((a) => (
                            <li key={a}>{a}</li>
                        ))}
                    </ul>
                    <div className="proof__stats">
                        <div>
                            <strong>720+</strong>
                            <span>ready-made rotation templates</span>
                        </div>
                        <div>
                            <strong>1,000+</strong>
                            <span>team Quick Setup codes</span>
                        </div>
                        <div>
                            <strong>4 digits</strong>
                            <span>to load your whole schedule</span>
                        </div>
                        <div>
                            <strong>Free</strong>
                            <span>on iPhone, iPad &amp; Android</span>
                        </div>
                    </div>
                </Container>
            </section>

            {/* ── How it works ─────────────────────────────────────── */}
            <section className="section steps">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">How it works</span>
                        <h2>Up and running in a minute</h2>
                    </div>
                    <ol className="steps__list">
                        {steps.map((s, i) => (
                            <li key={s.title}>
                                <span className="steps__num">{i + 1}</span>
                                <h3>{s.title}</h3>
                                <p>{s.body}</p>
                            </li>
                        ))}
                    </ol>
                </Container>
            </section>

            {/* ── Popular schedules ────────────────────────────────── */}
            <section id="schedules" className="section schedules">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">Popular schedules</span>
                        <h2>Works with the schedule you already have</h2>
                        <p>
                            The 10 most common rotations, most popular first. Load one from 720+ ready-made
                            templates, or enter your own pattern in seconds.
                        </p>
                    </div>
                    <ol className="schedules__grid">
                        {popularSchedules.map((sch, i) => (
                            <li key={sch.name} className="schedule-card">
                                <div className="schedule-card__top">
                                    <span className="schedule-card__rank">{i + 1}</span>
                                    <div>
                                        <h3>{sch.name}</h3>
                                        <span className="schedule-card__meta">
                                            {sch.aka ? `${sch.aka} · ` : ''}
                                            {sch.hours}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="schedule-card__strip"
                                    role="img"
                                    aria-label={`${sch.pattern.length}-day cycle`}
                                    // One row per cycle; long cycles like DuPont's 28 days split into two.
                                    style={{
                                        gridTemplateColumns: `repeat(${sch.pattern.length > 16 ? sch.pattern.length / 2 : sch.pattern.length}, minmax(0, 16px))`,
                                    }}
                                >
                                    {sch.pattern.split('').map((c, j) => (
                                        <span key={j} className={`cell cell--${c}`} />
                                    ))}
                                </div>
                                <span className="schedule-card__cycle">{sch.pattern.length}-day cycle</span>
                                <p>{sch.body}</p>
                                <span className="schedule-card__used">{sch.usedBy}</span>
                            </li>
                        ))}
                    </ol>
                    <div className="schedules__foot">
                        <ul className="schedules__legend" aria-label="Legend">
                            <li><span className="cell cell--D" /> Day</li>
                            <li><span className="cell cell--N" /> Night</li>
                            <li><span className="cell cell--F" /> 24 hours</li>
                            <li><span className="cell cell--O" /> Off</li>
                        </ul>
                        <div className="schedules__help">
                            <p>Don't see yours? ShiftWave repeats any pattern you enter, however long it is.</p>
                            <p>
                                Don't want to enter it yourself? Send us the list of days & your start date
                                (or a picture of your schedule), and we'll set it up for you.{' '}
                                <a href="mailto:myprojectxinc@gmail.com?subject=Please%20set%20up%20my%20schedule">
                                    Email us
                                </a>
                            </p>
                        </div>
                    </div>
                </Container>
            </section>

            {/* ── Feature showcases ────────────────────────────────── */}
            <section id="features" className="section showcases">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">Features</span>
                        <h2>Everything your schedule needs</h2>
                        <p>Made for the way shift workers actually plan their lives: fast to set up, easy to read, and always with you.</p>
                    </div>

                    {showcases.map((f, i) => (
                        <article key={f.id} id={f.id} className={`showcase${i % 2 ? ' showcase--flip' : ''}`}>
                            <div className="showcase__media">
                                <PhoneShot shot={f.shot} crop alt={`ShiftWave ${f.title} screen`} />
                            </div>
                            <div className="showcase__copy">
                                <span className="eyebrow">
                                    {f.eyebrow}
                                    {f.premium && <PremiumTag />}
                                </span>
                                <h3>{f.title}</h3>
                                <p>{f.body}</p>
                                <ul className="check-list">
                                    {f.points.map((p) => (
                                        <li key={p}>
                                            <CheckRoundedIcon />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </Container>
            </section>

            {/* ── More features ────────────────────────────────────── */}
            <section className="section extras">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">And more</span>
                        <h2>The little things that make it easy</h2>
                    </div>
                    <div className="extras__grid">
                        {extras.map((e) => (
                            <div key={e.title} className="extras__card">
                                <span className="extras__icon">{extraIcons[e.icon]}</span>
                                <h3>
                                    {e.title}
                                    {e.premium && <PremiumTag />}
                                </h3>
                                <p>{e.body}</p>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* ── Video tour ───────────────────────────────────────── */}
            <section id="video" className="section tour">
                <Container maxWidth="lg" className="tour__inner">
                    <div className="tour__media">
                        <video
                            controls
                            playsInline
                            preload="none"
                            poster="/images/shiftwave/tour_poster.jpg"
                            width={720}
                            height={1560}
                            onPlay={() => {
                                const gtag = (window as any).gtag;
                                if (typeof gtag === 'function') gtag('event', 'video_play', { video: 'shiftwave_tour' });
                            }}
                        >
                            <source src="/images/shiftwave/tour.mp4" type="video/mp4" />
                        </video>
                    </div>
                    <div className="tour__copy">
                        <span className="eyebrow">Video tour</span>
                        <h2>See ShiftWave in 25 seconds</h2>
                        <p>Watch how quickly you can set up a rotation, add notes and color-code your days.</p>
                        <ul className="check-list">
                            <li>
                                <CheckRoundedIcon />
                                Add notes to any day
                            </li>
                            <li>
                                <CheckRoundedIcon />
                                Set your start date and repeating days
                            </li>
                            <li>
                                <CheckRoundedIcon />
                                Highlight text in your favorite colors
                            </li>
                        </ul>
                        <StoreBadges app={shiftwave} placement="video" size="md" />
                    </div>
                </Container>
            </section>

            {/* ── Screenshot gallery ───────────────────────────────── */}
            <section className="section gallery" aria-label="ShiftWave screenshots">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">Screenshots</span>
                        <h2>See it in action</h2>
                    </div>
                </Container>
                <div className="gallery__track" tabIndex={0}>
                    {showcases.map((f) => (
                        <PhoneShot key={f.id} shot={f.shot} alt={`${f.title}: ${f.body}`} className="gallery__item" />
                    ))}
                </div>
            </section>

            {/* ── Pricing ──────────────────────────────────────────── */}
            <section id="pricing" className="section pricing">
                <Container maxWidth="lg">
                    <div className="section__head">
                        <span className="eyebrow">Pricing</span>
                        <h2>Free to start. Premium when you want more.</h2>
                    </div>
                    <div className="pricing__grid">
                        <div className="plan">
                            <h3>Free</h3>
                            <p className="plan__sub">Everything you need to see your schedule.</p>
                            <ul className="check-list">
                                {freeFeatures.map((f) => (
                                    <li key={f}>
                                        <CheckRoundedIcon />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <p className="plan__note">Supported by ads.</p>
                        </div>
                        <div className="plan plan--premium">
                            <h3>
                                Premium <StarRoundedIcon />
                            </h3>
                            <p className="plan__sub">Everything in Free, plus:</p>
                            <ul className="check-list">
                                {premiumFeatures.map((f) => (
                                    <li key={f}>
                                        <CheckRoundedIcon />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <p className="plan__note">Monthly, annual or one-time lifetime access, right in the app.</p>
                        </div>
                    </div>
                </Container>
            </section>

            {/* ── CopApp! ──────────────────────────────────────────── */}
            <section id="copapp" className="section copapp">
                <Container maxWidth="lg">
                    <div className="copapp__card">
                        <div className="copapp__intro">
                            <img src={copapp.icon} alt="CopApp! icon" width={96} height={96} />
                            <div>
                                <span className="eyebrow">Also from MyProjectX</span>
                                <h2>CopApp!</h2>
                                <p className="copapp__tagline">The original shift schedule app.</p>
                            </div>
                        </div>
                        <div className="copapp__body">
                            <p>
                                {copapp.description} CopApp! has helped officers and shift workers keep track of their
                                rotations for years, and it's still available on iPhone and Android.
                            </p>
                            <ul className="copapp__features">
                                {copapp.features.slice(0, 6).map((f) => (
                                    <li key={f}>
                                        <CheckRoundedIcon />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <StoreBadges app={copapp} placement="copapp" size="md" />
                            <div className="copapp__links">
                                <a href="/copapp/help.htm">CopApp! help</a>
                                <a href="/copapp/whats_new.htm">What's new</a>
                            </div>
                        </div>
                        <div className="copapp__upgrade">
                            <div>
                                <strong>Ready for the next generation?</strong>
                                <span>
                                    ShiftWave uses the same Quick Setup codes as CopApp!, so you can move your schedule
                                    over in seconds.
                                </span>
                            </div>
                            <a href="#download" className="text-link">
                                Get ShiftWave <ArrowForwardRoundedIcon />
                            </a>
                        </div>
                    </div>
                </Container>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────── */}
            <section id="faq" className="section faq">
                <Container maxWidth="md">
                    <div className="section__head">
                        <span className="eyebrow">FAQ</span>
                        <h2>Questions, answered</h2>
                    </div>
                    {faqs.map((f) => (
                        <Accordion key={f.q} disableGutters className="faq__item">
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <h3>{f.q}</h3>
                            </AccordionSummary>
                            <AccordionDetails>
                                <p>{f.a}</p>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <p className="faq__more">
                        Still have a question? Email us at{' '}
                        <a href="mailto:myprojectxinc@gmail.com">myprojectxinc@gmail.com</a>.
                    </p>
                </Container>
            </section>

            {/* ── Final call to action ─────────────────────────────── */}
            <section id="download" className="section download">
                <Container maxWidth="lg">
                    <div className="download__card">
                        <div className="download__copy">
                            <img src={shiftwave.icon} alt="" width={72} height={72} />
                            <h2>Throw away your paper schedule.</h2>
                            <p>Download ShiftWave free and set up your rotation in a minute.</p>
                            <StoreBadges app={shiftwave} placement="footer_cta" />
                        </div>
                        <div className="download__qr">
                            <img src={qrCodeUrl('shiftwave', 200)} alt="QR code to download ShiftWave" width={200} height={200} loading="lazy" />
                            <span>Scan with your phone's camera to download</span>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default Home;
